import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Sparkles, BadgeCheck } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LINES = [
  ["Wholesale"],
  ["Housekeeping", "&"],
  ["Stationery", "Suppliers."],
];

function MaskedLine({ words, index }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay: 0.25 + index * 0.14, ease: EASE }}
      >
        {words.map((w, i) =>
          w === "&" ? (
            <span key={i} className="font-serif italic font-medium text-brand-green"> & </span>
          ) : (
            <span key={i}>{w} </span>
          )
        )}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-[92vh] flex items-center pt-28 pb-16 overflow-hidden">
      <motion.div style={{ y: bgY }} className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-brand-blue/10 blur-3xl" />
        <div className="absolute bottom-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-brand-green/10 blur-3xl" />
        <span className="absolute top-24 left-[8%] font-serif italic text-[11rem] leading-none text-outline opacity-40 select-none hidden xl:block">Sy</span>
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-brand-green/30 bg-brand-green/10 px-4 py-1.5 text-xs font-bold tracking-widest uppercase text-brand-greenDark"
            data-testid="hero-badge"
          >
            <Sparkles size={13} />
            Sevayog Facilities Pvt Ltd — Pune
          </motion.div>

          <h1 data-testid="hero-headline" className="mt-6 font-display font-black tracking-tighter leading-[0.95] text-5xl md:text-6xl lg:text-7xl text-slate-900">
            {LINES.map((words, i) => (
              <MaskedLine key={i} words={words} index={i} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-slate-600"
            data-testid="hero-subtext"
          >
            Your single B2B partner for housekeeping consumables, office stationery and corporate
            procurement — and the proud home of <span className="font-bold text-brand-blue">Cleenvo</span>,
            our own range of cleaning essentials sold at <span className="font-bold text-brand-greenDark">officiaa.com</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#cleenvo"
              data-testid="hero-explore-products-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-brand-greenDark hover:-translate-y-0.5 shadow-[0_12px_30px_-10px_rgba(91,168,41,0.6)]"
            >
              Explore Products
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href="#contact"
              data-testid="hero-contact-btn"
              className="group inline-flex items-center gap-2 rounded-full border-2 border-brand-blue px-7 py-3 text-sm font-bold text-brand-blue transition-all duration-200 hover:bg-brand-blue hover:text-white hover:-translate-y-0.5"
            >
              Contact Us
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-stats"
          >
            {[["8+", "Cleenvo products"], ["500 ml – 5 L", "pack sizes"], ["Pan-India", "delivery"]].map(([v, l]) => (
              <div key={l}>
                <div className="font-display text-2xl font-extrabold text-brand-blue">{v}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-0.5">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
          className="lg:col-span-5 relative"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div className="overflow-hidden rounded-[2.5rem] rounded-tr-[5rem] border border-slate-200 shadow-[0_40px_80px_-30px_rgba(18,58,102,0.35)]">
              <img
                src="/assets/hero-cleaning.jpg"
                alt="Cleenvo housekeeping cleaning supplies"
                className="h-[420px] lg:h-[500px] w-full object-cover"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-4 md:-left-8 top-10 flex items-center gap-2.5 rounded-2xl bg-white/90 backdrop-blur-md border border-slate-200 px-4 py-3 shadow-xl"
              data-testid="hero-chip-cleenvo"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-green/15 text-brand-greenDark">
                <BadgeCheck size={18} />
              </span>
              <div>
                <div className="text-sm font-bold text-slate-900">Cleenvo</div>
                <div className="text-[11px] text-slate-500">Our own brand</div>
              </div>
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
              className="absolute -right-3 md:-right-6 bottom-12 flex items-center gap-2.5 rounded-2xl bg-white/90 backdrop-blur-md border border-slate-200 px-4 py-3 shadow-xl"
              data-testid="hero-chip-bulk"
            >
              <img src="/assets/products/A1C.png" alt="Cleenvo floor cleaner" className="h-10 w-10 object-contain" />
              <div>
                <div className="text-sm font-bold text-slate-900">Bulk Pricing</div>
                <div className="text-[11px] text-slate-500">for B2B buyers</div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
